import clsx from 'clsx';
import { isEmpty, map, uniqBy, values } from 'lodash';
import { useEffect, useMemo } from 'react';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';

import {
  GiftcardPromote,
  LeisurePromote,
  Skeleton,
  Slide,
  Slider,
  TravelPromote,
  useHomePageContext
} from 'components';
import { PAGE_TYPES } from 'constants';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

import 'assets/styles/components/products/promote-slider.scss';

const sliderOptions = {
  perPage: 4,
  breakpoints: {
    1920: {
      perPage: 3
    },
    1024: {
      perPage: 2
    },
    640: {
      perPage: 1,
      padding: { right: '20%' }
    }
  },
  perMove: 1,
  gap: '1.5rem',
  padding: { right: '10%' }
};

const slots = {
  travels: 'INDEX_FEATURED',
  leisures: 'NEWS_GOOD_DEAL',
  sport: 'INDEX_ACCES'
};

const CATALOGUES = {
  giftcards: 'Buybox',
  touloisirs: 'Touloisirs',
  cogas: 'Cogas',
  ticketmaster: 'Ticketmaster'
};

const PromoteProducts = () => {
  const navigate = useNavigate();

  const { currentUser } = useUser();
  const {
    call: promotesCall,
    data: products,
    loading: promotesLoading,
    setData: setProducts
  } = useFetch({ withSetState: false });
  const { call: giftcardCall, loading: giftcardLoading } = useFetch({ withSetState: false });
  const { call: giftcardPromotesSliderCall, loading: promotesSliderLoading } = useFetch({ withSetState: false });

  const { pageType, setPromoteTitle, promoteTitle } = useHomePageContext();
  const sliderType = useMemo(() => {
    return products?.promotes?.length > sliderOptions.perPage ? 'loop' : 'slide';
  }, [pageType, products]);

  const loading = useMemo(() => {
    return giftcardLoading || promotesSliderLoading || promotesLoading;
  }, [giftcardLoading, promotesSliderLoading, promotesLoading]);

  useEffect(() => {
    pageType === PAGE_TYPES.giftcards ? getGiftcardPromotes() : getPromotes();
  }, [pageType]);

  const getPromotes = async () => {
    if (!currentUser?.serialNumber) return;

    const { data } =
      (await promotesCall({
        service: 'castelis',
        method: 'get',
        path: `/product/promotes/${currentUser.serialNumber}`,
        query: { slot: slots[pageType] }
      })) || {};

    if (!isEmpty(data?.body?.promotes)) {
      let promotes = map(data?.body?.promotes, (promote) => {
        if (values(promote).every((value) => !value)) return null;
        return promote;
      });
      promotes = promotes.filter(Boolean);
      setProducts({ promotes });
    }
  };

  const getGiftcardPromotes = async () => {
    const { data: sliderData } =
      (await giftcardPromotesSliderCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/promotes-sliders',
        query: {
          filters: {
            homepages: {
              page_type: {
                name: PAGE_TYPES.giftcards
              }
            }
          }
        }
      })) || {};

    if (!isEmpty(sliderData?.body?.data?.[0])) {
      setPromoteTitle(sliderData?.body?.data[0]?.title);
      formatGiftcardProducts(sliderData?.body?.data[0]?.promotes || []);
    }
  };

  const formatGiftcardProducts = async (sliderPromotes) => {
    // IDs of promotes that must be fetched from Strapi instead of Slider
    const promoteIds = [];

    // Products that have initially all informations without needing to fetch from catalogue
    let promoteProducts = [];

    sliderPromotes.forEach((promote) => {
      // Check if promote has all informations provided in slider before fetching informations from catalogue
      if (promote.picture && promote.title && promote.category) {
        promoteProducts.push({ ...promote, category: { name: promote.category } });
        return;
      }

      // Currently, only giftcards are fetched from Strapi catalogue
      if (promote.catalogue === CATALOGUES.giftcards && promote?.productId) {
        promoteIds.push(promote.productId);
      }
    });

    if (promoteIds.length > 0) {
      // Get all giftcards from Strapi to get complementary informations
      const { data: giftcardData } =
        (await giftcardCall({
          service: appService.getStrapiServiceName(),
          method: 'get',
          path: '/api/giftcards',
          query: {
            filters: {
              gift_card_id: { $in: promoteIds }
            }
          }
        })) || {};

      if (!isEmpty(giftcardData?.body?.data)) {
        // Override data from Slider with promotes from catalogue
        const formattedGiftcardPromotes = giftcardData.body.data.map((giftcard) => {
          const promoteFromSlider = sliderPromotes.find((promote) => promote.productId === giftcard.giftCardId);
          const title = promoteFromSlider?.title || giftcard.title;
          const image = promoteFromSlider?.image || giftcard.picture;
          const category = promoteFromSlider?.category ? { name: promoteFromSlider?.category } : giftcard.category;
          return { ...giftcard, title, image, category, productId: giftcard.giftCardId };
        });

        promoteProducts = uniqBy([...promoteProducts, ...formattedGiftcardPromotes], 'productId');
      }
    }

    setProducts({ promotes: promoteProducts });
  };

  const goToProductSow = (product) => {
    let path = '';
    switch (pageType) {
      case PAGE_TYPES.travels:
        path = routes.travelShow.path;
        break;
      case PAGE_TYPES.leisures:
        path = routes.leisureShow.path;
        break;
      case PAGE_TYPES.sport:
        path = routes.sportShow.path;
        break;
      case PAGE_TYPES.giftcards:
        path = routes.giftcardShow.path;
        break;
      default:
        break;
    }

    if (product?.productId) {
      const params =
        pageType === PAGE_TYPES.giftcards ? '' : createSearchParams({ typeItem: product.typeItem }).toString();
      const productPath = generatePath(path, { id: product.productId });
      navigate({ pathname: productPath, search: params });
    }
  };

  return (
    <div className={clsx('promote-slider lg:pt-12', { hidden: !loading && products?.promotes?.length === 0 })}>
      {loading && (
        <Slider type="loop" options={sliderOptions} sliderClass="with-card-shadow absolute-arrows top-right">
          <Slide slideClass="rounded-lg shadow-lg overflow-hidden">
            <div className="aspect-square sm:aspect-video">
              <Skeleton classNames="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col gap-4 p-6">
              <Skeleton classNames="w-full h-4" />
              <Skeleton classNames="w-full h-4" />
            </div>
          </Slide>
        </Slider>
      )}

      {!loading && products?.promotes?.length > 0 && (
        <>
          {promoteTitle && <h2 className="h2 underlined-yellow mb-12">{promoteTitle}</h2>}

          <Slider type={sliderType} options={sliderOptions} sliderClass="with-card-shadow absolute-arrows top-right">
            {products.promotes.map((promote, i) => (
              <Slide key={`promote-slider-${i}`}>
                {[PAGE_TYPES.leisures, PAGE_TYPES.sport].includes(pageType) && (
                  <LeisurePromote promote={promote} goToProductSow={() => goToProductSow(promote)} />
                )}
                {pageType === PAGE_TYPES.travels && (
                  <TravelPromote promote={promote} goToProductSow={() => goToProductSow(promote)} />
                )}
                {pageType === PAGE_TYPES.giftcards && (
                  <GiftcardPromote promote={promote} goToProductSow={() => goToProductSow(promote)} />
                )}
              </Slide>
            ))}
          </Slider>
        </>
      )}
    </div>
  );
};

export default PromoteProducts;
